<template>
  <div>
    <v-row>
      <v-col class="paper-side">
        <v-container id="explorer" fluid class="explorer-container">
          <div class="pane" @mousedown.stop>
            <div v-if="currentSnapshot" id="paper" ref="canvas" class="paper-container">
              <minimap />

              <artifact-type-container-menu />
              <artifact-container-menu />
            </div>
          </div>
        </v-container>
      </v-col>
      <div>
        <v-navigation-drawer
          id="navigation-right"
          :width="CONSTANTS.EXPLORER.SIDEBAR_RIGHT_WIDTH"
          permanent
          location="right"
          :rail="rail"
        >
          <template #default>
            <v-card v-show="!rail" style="padding-right: 4px" flat>
              <v-expansion-panels id="accordions" v-model="accordions" variant="accordion" multiple>
                <v-expansion-panel id="panel1" ref="artifactSearch" value="artifactPanel">
                  <v-expansion-panel-title style="height: 30px" @click="toggleSearchPanel()">{{
                    labels.SEARCH_VIEW.ARTIFACT_SEARCH
                  }}</v-expansion-panel-title>
                  <v-expansion-panel-text :eager="true"
                    ><search-view :height="panelHeights.artifactPanel" /></v-expansion-panel-text
                ></v-expansion-panel>

                <v-expansion-panel id="panel2" ref="viewProperties" value="propertiesPanel">
                  <v-expansion-panel-title style="height: 30px" @click="togglePropertiesPanel()"
                    >{{ labels.PROPERTIES_VIEW.PROPERTIES }}
                  </v-expansion-panel-title>
                  <v-expansion-panel-text :eager="true"
                    ><properties-view
                      :height="panelHeights.propertiesPanel" /></v-expansion-panel-text
                ></v-expansion-panel>
              </v-expansion-panels>
            </v-card>
          </template>
          <template #append>
            <div class="pa-2">
              <v-btn
                v-if="!rail"
                block
                class="text-none collapse-btn"
                size="large"
                variant="text"
                :prepend-icon="CONSTANTS.ICONS_MDI.ICON_CHEVRON_RIGHT"
                @click="toggleRail"
              >
                <template #prepend>
                  <v-icon class="pr-5 collapse-icon" size="large" />
                </template>

                {{ labels.EXPLORER.COLLAPSE_MENU }}
              </v-btn>

              <v-btn-group v-if="rail" class="expand-btn-group" variant="outlined">
                <v-btn
                  :icon="CONSTANTS.ICONS_MDI.ICON_CHEVRON_LEFT"
                  block
                  class="expand-btn"
                  @click="toggleRail"
                />
              </v-btn-group>
            </div>
          </template>
        </v-navigation-drawer>
      </div>
    </v-row>
  </div>
</template>

<script lang="ts" setup>
  import PropertiesView from '@/components/RappidWidgets/PropertiesView/PropertiesView.vue'
  import SearchView from '@/components/RappidWidgets/SearchView.vue'
  import Minimap from '@/components/RappidWidgets/Minimap/Minimap.vue'
  import ArtifactContainerMenu from '@/components/RappidWidgets/ContainerMenu/ArtifactContainerMenu.vue'
  import ArtifactTypeContainerMenu from '@/components/RappidWidgets/ContainerMenu/ArtifactTypeContainerMenu.vue'
  import { ref, reactive } from 'vue'
  import CONSTANTS from '@/constants/Config.constants'
  import lbls from '@/translations/labels.json'
  import { useRegisterExplorer } from './composables/register'

  import { useSnapshotStore } from '@/stores/snapshotStore'
  import { storeToRefs } from 'pinia'
  import { useEventBus } from '@/utils/events'
  import { EventBus } from '@/event-bus'

  const { currentSnapshot } = storeToRefs(useSnapshotStore())

  const canvas = ref<HTMLDivElement>(document.createElement('div'))

  // The main entry point to registering the JointJS explorer view to this component
  useRegisterExplorer(canvas)

  const labels = lbls
  const accordions = ref(['0', '1'])
  const artifactSearch = ref<HTMLElement>()
  const viewProperties = ref<HTMLElement>()
  const screenHeight = ref(innerHeight)

  const searchExpanded = ref(true)
  const propertiesExpanded = ref(true)

  const panelHeights = reactive({
    artifactPanel: screenHeight.value * 0.35,
    propertiesPanel: screenHeight.value * 0.5,
  })

  const rail = ref(false)

  function toggleRail(): void {
    rail.value = !rail.value
    EventBus.emit('toggleExplorerSidebar', rail.value)
  }

  function adjustPanelHeights(): void {
    if (searchExpanded.value) {
      if (propertiesExpanded.value) {
        panelHeights.artifactPanel = screenHeight.value * 0.35
        panelHeights.propertiesPanel = screenHeight.value * 0.35
      } else {
        panelHeights.artifactPanel = screenHeight.value * 0.6
        panelHeights.propertiesPanel = screenHeight.value * 0.1
      }
    } else {
      if (propertiesExpanded.value) {
        panelHeights.artifactPanel = screenHeight.value * 0.1
        panelHeights.propertiesPanel = screenHeight.value * 0.6
      } else {
        panelHeights.artifactPanel = screenHeight.value * 0.35
        panelHeights.propertiesPanel = screenHeight.value * 0.35
      }
    }
  }

  useEventBus('searchedArtifacts', () => {
    adjustPanelHeights()
  })

  function toggleSearchPanel(): void {
    searchExpanded.value = !searchExpanded.value
    adjustPanelHeights()
  }

  function togglePropertiesPanel(): void {
    propertiesExpanded.value = !propertiesExpanded.value
    adjustPanelHeights()
  }
</script>

<style lang="scss">
  @use '../../../node_modules/@clientio/rappid/rappid.css';
  @use '../../assets/styles/variables';

  .paper-container {
    top: 0;
    left: 0;
    right: 0;
    height: calc(100vh - 94px);
    width: 100%;

    .joint-paper {
      border: 1px solid rgb(var(--v-theme-background));
    }
  }

  .pane {
    height: 100%;
    width: 100%;
  }

  .popup-header {
    display: inline;
    font-weight: bold;
  }

  .popup-value {
    display: inline;
    left: 1em;
  }

  .view-title {
    color: white;
    background-color: var(--v-theme-primary);
    font-size: 16px;
  }

  .joint-paper-scroller[data-cursor='grab'] {
    overflow: hidden;
  }

  .joint-paper > svg,
  .joint-paper-background,
  .joint-paper-grid {
    position: absolute;
    top: 0;
    left: 0;
    right: 1em;
    bottom: 0;
  }
</style>

<style lang="scss" scoped>
  @use '../../assets/styles/variables';

  .explorer-sidemenu {
    height: 95%;
  }

  .explorer-container {
    padding: 0;
    width: 100%;
    max-width: max-content;
  }
  .expand-btn {
    color: rgb(var(--v-theme-grey-lighty));
    &:hover {
      color: rgb(var(--v-theme-font-color));
    }
  }

  .expand-btn-group {
    max-width: 40px;
    max-height: 40px;
    border-color: rgb(var(--v-theme-grey-light));
    border-radius: 5px;
    border-width: 1px;
  }

  .collapse-btn {
    width: 300px;
    color: rgb(var(--v-theme-font-color));
  }

  .collapse-icon {
    color: rgb(var(--v-theme-grey-dark));
  }
  .active-sidebar-item {
    background-color: rgb(var(--v-theme-itemis-analyze-primary));
    color: rgb(var(--v-theme-surface));

    .sidebar-item-title {
      color: rgb(var(--v-theme-surface));
    }
  }

  .custom-tooltip :deep(.v-overlay__content) {
    background: rgb(var(--v-theme-itemis-analyze-secondary));
    color: rgb(var(--v-theme-font-color));
  }
  .sidebar-item {
    &:hover {
      background-color: rgb(var(--v-theme-itemis-analyze-primary));
      color: rgb(var(--v-theme-surface));

      .sidebar-item-title {
        color: rgb(var(--v-theme-surface));
      }
    }
  }
  .v-expansion-panel-text:deep(.v-expansion-panel-text__wrapper) {
    padding: -0px;
  }
  .v-expansion-panel--active > .v-expansion-panel-title {
    min-height: 40px !important;
  }
</style>

<style src="./joint.css" lang="css" />
